import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import Button from '@welldev-group/rcl-button';
import Card from '@welldev-group/rcl-card';
import Icon from '@welldev-group/rcl-icon';
import { I18nText } from '@welldev-group/react-comp-lib';
import ContentLoaderPlaceholer from '@welldev-group/rcl-content-loader-placeholder';
import SanitizedHTML from '../../../../shared/components/SanitizedHTML';
import TimeAgo from '../../../../shared/components/TimeAgo';

const AffiliateBriefing = props => {
  const [userModifiesBriefing, setUserModifiesBriefing] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const isBriefingAvailable = value =>
    !value ||
    value === '<p><br></p>' ||
    (value && !value.replace(/<[^>]*>/g, '').trim());

  const noBriefingAvailableFromProps = isBriefingAvailable(props.briefing);
  const noBriefingAvailableFromState = isBriefingAvailable(briefing);

  const onClickChange = () => {
    setUserModifiesBriefing(true);
    setBriefing(props.briefing);
  };

  const onClickCancel = () => {
    setUserModifiesBriefing(false);
    setBriefing(null);
  };

  const onClickSave = () => {
    setUserModifiesBriefing(false);

    if (briefing !== props.briefing) {
      setIsLoading(true);

      const params = {
        affiliate: {
          briefing,
        },
      };

      props.updateAffiliate(params);
    }
  };

  const onBriefingChange = text => {
    setBriefing(text);
  };

  useEffect(() => {
    setIsLoading(false);
  }, [props.briefing]);

  const toolbarOptions = [
    ['bold', 'italic', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
  ];

  return (
    <Card version="v2" size="full" className="affiliate-briefing__card">
      {userModifiesBriefing ? (
        <>
          <div className="affiliate-briefing__head">
            <I18nText id="affiliate_three_sixty.affiliate.briefing.header" />
          </div>
          <ReactQuill
            placeholder={props.placeholder}
            modules={{
              toolbar: toolbarOptions,
            }}
            theme="snow"
            value={briefing}
            onChange={onBriefingChange}
            preserveWhitespace={false}
          />
          <div className="affiliate-briefing__actions">
            <Button
              label={
                <I18nText id="affiliate_three_sixty.affiliate.button_label.save" />
              }
              version="v2"
              onClick={onClickSave}
              size="small"
              type="primary"
              disabled={
                noBriefingAvailableFromProps && noBriefingAvailableFromState
              }
            />
            <Button
              label={
                <I18nText id="affiliate_three_sixty.affiliate.button_label.cancel" />
              }
              version="v2"
              onClick={onClickCancel}
              size="small"
            />
          </div>
        </>
      ) : (
        <>
          <div id="affiliate-briefing__text">
            {noBriefingAvailableFromProps && noBriefingAvailableFromState ? (
              <div className="affiliate-briefing__section">
                <Icon
                  size="large"
                  color="tertiary"
                  name="stickyNote"
                  showBGColor={true}
                />
                <div className="affiliate-briefing__header">
                  <I18nText id="affiliate_three_sixty.affiliate.briefing.headerText" />
                </div>
                <div className="affiliate-briefing__sub-header">
                  <div>
                    <I18nText id="affiliate_three_sixty.affiliate.briefing.subHeader" />
                  </div>
                  <div className="affiliate-briefing__sub-header--name">
                    {props.name}
                  </div>
                </div>
                <div className="affiliate-briefing__change-button">
                  <Button
                    label={
                      <I18nText id="affiliate_three_sixty.affiliate.button_label.add" />
                    }
                    version="v2"
                    onClick={onClickChange}
                    size="tiny"
                    type="primary"
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="affiliate-briefing__wrapper">
                  <div>
                    <div className="affiliate-briefing__head">
                      <I18nText id="affiliate_three_sixty.affiliate.briefing.header" />
                    </div>
                    <div className="affiliate-briefing__time">
                      {isLoading ? (
                        <ContentLoaderPlaceholer
                          numberOfLines={1}
                          showBackground={false}
                        />
                      ) : (
                        <>
                          <I18nText id="affiliate_three_sixty.affiliate.briefing.last_updated" />
                          <TimeAgo
                            datetime={props.briefingUpdatedAt}
                            withShortFormat={true}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="affiliate-briefing__change-button">
                    <Button
                      label={
                        <I18nText id="affiliate_three_sixty.affiliate.button_label.edit" />
                      }
                      version="v2"
                      onClick={onClickChange}
                      size="tiny"
                      type="primary"
                    />
                  </div>
                </div>
                {isLoading ? (
                  <ContentLoaderPlaceholer
                    numberOfLines={1}
                    showBackground={false}
                  />
                ) : (
                  <SanitizedHTML
                    html={userModifiesBriefing ? briefing : props.briefing}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

AffiliateBriefing.defaultProps = {
  placeholder: null,
};

AffiliateBriefing.propTypes = {
  name: PropTypes.string.isRequired,
  briefing: PropTypes.string.isRequired,
  briefingUpdatedAt: PropTypes.string.isRequired,
  updateAffiliate: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default AffiliateBriefing;
